import React, { useState, useEffect, useRef } from 'react';
import { getPublicUser, getRestaurant, removeFromFriends, removeFromBlocked, listenToUser } from '../services/DataStore';
import { PublicUser } from '../models/PublicUser';
import { Restaurant } from '../models/Restaurant';
import { useAuth } from '../contexts/AuthContext';

const FriendsList = ({ friendIds, title, onClose }) => {
  const { userData, currentUser } = useAuth();
  const [friends, setFriends] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredFriend, setHoveredFriend] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [favoriteRestaurants, setFavoriteRestaurants] = useState(new Map());
  const [activeTab, setActiveTab] = useState('friends'); // 'friends' or 'blocked'
  const hideTimeoutRef = useRef(null);
  const modalRef = useRef(null);
  const listRef = useRef(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);

  // Set up listener for current user's data
  useEffect(() => {
    let userUnsubscribe = null;

    if (currentUser?.uid) {
      console.log('Setting up user listener for FriendsList:', currentUser.uid);
      userUnsubscribe = listenToUser(currentUser.uid, (updatedUserData) => {
        console.log('FriendsList received user update:', updatedUserData);
        if (updatedUserData) {
          // Update friends list
          const updatedFriendIds = updatedUserData.getFriends();
          loadFriends(updatedFriendIds);

          // Update blocked users list
          const updatedBlockedIds = updatedUserData.getBlockedusers();
          loadBlockedUsers(updatedBlockedIds);
        }
      });
    }

    return () => {
      if (userUnsubscribe) {
        console.log('Cleaning up user listener in FriendsList');
        userUnsubscribe();
      }
    };
  }, [currentUser?.uid]);

  const loadFriends = async (ids) => {
    try {
      const friendPromises = ids.map(id => getPublicUser(id));
      const loadedFriends = await Promise.all(friendPromises);
      setFriends(loadedFriends.filter(f => f !== null && f instanceof PublicUser));
    } catch (error) {
      console.error('Error loading friends:', error);
    }
  };

  const loadBlockedUsers = async (ids) => {
    try {
      if (ids && ids.length > 0) {
        const blockedPromises = ids.map(id => getPublicUser(id));
        const loadedBlocked = await Promise.all(blockedPromises);
        const validBlockedUsers = loadedBlocked.filter(f => f !== null && f instanceof PublicUser);
        setBlockedUsers(validBlockedUsers);
      } else {
        setBlockedUsers([]);
      }
    } catch (error) {
      console.error('Error loading blocked users:', error);
    }
  };

  // Initial load of users
  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        // Load friends
        await loadFriends(friendIds);

        // Load blocked users
        if (userData?.getBlockedusers) {
          const blockedIds = userData.getBlockedusers();
          await loadBlockedUsers(blockedIds);
        }
      } catch (error) {
        console.error('Error loading users:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [friendIds, userData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the modal, list, and popup
      const isOutsideModal = modalRef.current && !modalRef.current.contains(event.target);
      const popup = document.querySelector('.friend-popup');
      const isOutsidePopup = !popup || !popup.contains(event.target);
      
      if (isOutsideModal && isOutsidePopup) {
        console.log('Click outside detected, closing list');
        onClose?.();
      }
    };

    // Use mousedown instead of click for better touch support
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [onClose]);

  const handleMouseEnter = (friend, event) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const listRect = listRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    let x = listRect.right - 400;
    let y = rect.top;
    
    if (spaceBelow < 300 && spaceAbove > 300) {
      y = rect.bottom - 300;
    }
    
    setPopupPosition({ x, y });
    setHoveredFriend(friend);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setHoveredFriend(null);
    }, 300);
  };

  // Load favorite restaurants when hoveredFriend changes
  useEffect(() => {
    const loadFavoriteRestaurants = async () => {
      if (!hoveredFriend?.favoriterestaurants) return;

      const newRestaurants = new Map(favoriteRestaurants);
      const unloadedIds = hoveredFriend.favoriterestaurants.filter(
        id => !newRestaurants.has(id)
      );

      if (unloadedIds.length === 0) return;

      try {
        const restaurantPromises = unloadedIds.map(id => getRestaurant(id));
        const loadedRestaurants = await Promise.all(restaurantPromises);
        
        loadedRestaurants.forEach((restaurant, index) => {
          if (restaurant instanceof Restaurant) {
            newRestaurants.set(unloadedIds[index], restaurant);
          }
        });

        setFavoriteRestaurants(newRestaurants);
      } catch (error) {
        console.error('Error loading favorite restaurants:', error);
      }
    };

    loadFavoriteRestaurants();
  }, [hoveredFriend]);

  const renderRestaurantCard = (restaurant) => {
    if (!restaurant) return null;

    return (
      <div className="flex flex-col p-2 bg-gray-50 rounded">
        <div className="flex items-center space-x-2">
          <img 
            src="/images/yelp_burst.svg"
            alt="Yelp"
            className="w-4 h-4"
          />
          <h4 className="text-sm font-medium text-gray-900 truncate">
            {restaurant.name}
          </h4>
          <span className="text-accent text-sm font-semibold ml-auto">
            {restaurant.price}
          </span>
        </div>
        <div className="mt-1 flex items-center space-x-2">
          <div className="flex items-center">
            <img 
              src={Restaurant.getRatingImage(restaurant.rating)}
              alt={`${restaurant.rating} stars`}
              className="h-3 w-auto"
            />
            <span className="ml-1 text-xs text-gray-600">
              ({restaurant.reviewCount})
            </span>
          </div>
          {restaurant.categories && restaurant.categories.length > 0 && (
            <span className="text-xs text-gray-500">
              {restaurant.categories[0].title}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderUserList = (users) => {
    if (loading) {
      return (
        <div className="p-4">
          <div className="animate-pulse space-y-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      );
    }

    if (users.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500">
          {activeTab === 'friends' ? 'No friends yet' : 'No blocked users'}
        </div>
      );
    }

    return (
      <div className="divide-y divide-gray-200">
        {users.map(user => (
          <div 
            key={user.id} 
            className="p-4 hover:bg-[#EBE4D2]"
            onMouseEnter={(e) => handleMouseEnter(user, e)}
            onMouseLeave={handleMouseLeave}
          >
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-full border-2 border-accent bg-gray-200 flex items-center justify-center overflow-hidden">
                {user.avatarurl ? (
                  <img 
                    src={user.avatarurl} 
                    alt={user.displayname}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="material-symbols-outlined text-gray-500">account_circle</span>
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 flex items-center h-12">
                  {user.displayname || 'Anonymous User'}
                </h3>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleRemoveClick = (user) => {
    setUserToRemove(user);
    setShowConfirmDialog(true);
  };

  const handleConfirmRemove = async () => {
    if (!userToRemove) return;

    try {
      if (activeTab === 'friends') {
        await removeFromFriends(userToRemove.id);
        setFriends(prev => prev.filter(f => f.id !== userToRemove.id));
      } else {
        await removeFromBlocked(userToRemove.id);
        setBlockedUsers(prev => prev.filter(f => f.id !== userToRemove.id));
      }
    } catch (error) {
      console.error('Error removing user:', error);
      alert('Failed to remove user. Please try again.');
    } finally {
      setShowConfirmDialog(false);
      setUserToRemove(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl max-w-2xl w-full h-[66vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Friends</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'friends'
                  ? 'text-accent border-b-2 border-accent'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('friends')}
            >
              Friends
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm ${
                activeTab === 'blocked'
                  ? 'text-accent border-b-2 border-accent'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('blocked')}
            >
              Blocked
            </button>
          </div>
        </div>

        {/* List */}
        <div ref={listRef} className="flex-1 overflow-y-auto">
          {activeTab === 'friends' ? renderUserList(friends) : renderUserList(blockedUsers)}
        </div>

        {/* Hover Popup */}
        {hoveredFriend && (
          <div 
            className="fixed z-50 w-[400px] h-[400px] friend-popup bg-white rounded-lg shadow-xl"
            style={{
              left: `${popupPosition.x}px`,
              top: `${popupPosition.y}px`
            }}
            onMouseEnter={() => {
              if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
              }
            }}
            onMouseLeave={handleMouseLeave}
          >
            <div className="p-6 h-full flex flex-col">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-20 h-20 rounded-full border-2 border-accent bg-gray-200 flex items-center justify-center overflow-hidden">
                  {hoveredFriend.avatarurl ? (
                    <img 
                      src={hoveredFriend.avatarurl} 
                      alt={hoveredFriend.displayname}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="material-symbols-outlined text-gray-500 text-4xl">account_circle</span>
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {hoveredFriend.displayname || 'Anonymous User'}
                  </h2>
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Favorite Restaurants</h3>
                <div className="overflow-y-auto h-[calc(100%-2rem)] pr-2">
                  {activeTab === 'blocked' ? (
                    <p className="text-gray-500 italic text-sm">Not available</p>
                  ) : hoveredFriend.favoriterestaurants && hoveredFriend.favoriterestaurants.length > 0 ? (
                    <div className="space-y-2">
                      {hoveredFriend.favoriterestaurants.map((restaurantId) => {
                        const restaurant = favoriteRestaurants.get(restaurantId);
                        return (
                          <div key={restaurantId}>
                            {restaurant ? (
                              renderRestaurantCard(restaurant)
                            ) : (
                              <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded animate-pulse">
                                <div className="w-4 h-4 bg-gray-200 rounded"></div>
                                <div className="h-4 bg-gray-200 rounded flex-1"></div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No favorite restaurants yet</p>
                  )}
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleRemoveClick(hoveredFriend)}
                  className="w-full bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">
                {activeTab === 'friends' ? 'Remove from friends?' : 'Remove from blocked?'}
              </h3>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowConfirmDialog(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmRemove}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendsList; 